import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { endOfDay } from 'date-fns';
import { ComponentProps, HTMLProps } from 'react';
import { BackdropProps } from '@mui/material';
import { AnalyticsEvents, trackEvent } from 'app/shared/u21-ui/analytics';
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
} from '@u21/tabler-icons';
import { DateRangeInputField } from 'app/shared/u21-ui/components/input/date-picker/DateRangeInputField';
import { U21TextFieldProps } from 'app/shared/u21-ui/components/input/text-field/U21TextFieldInputProps';
import {
  CustomizableFieldProps,
  DateRangeValue,
} from 'app/shared/u21-ui/components/input/date-picker/models';
import { getDOMProps } from 'app/shared/utils/react';
import { RangePickersDay } from 'app/shared/u21-ui/components/input/date-picker/RangePickersDay';

export interface U21DateRangePickerProps extends Omit<
  HTMLProps<HTMLDivElement>,
  'onChange' | 'value'
> {
  autoFocus?: boolean;
  backdrop?: boolean;
  backdropProps?: Omit<BackdropProps, 'open'>;
  clearable?: boolean;
  disabled?: boolean;
  endFieldProps?: CustomizableFieldProps;
  error?: boolean;
  id?: string;
  label?: string;
  maxDate?: Date;
  maxDateInputProps?: U21TextFieldProps['inputProps'];
  minDate?: Date;
  minDateInputProps?: U21TextFieldProps['inputProps'];
  onChange: (value: DateRangeValue) => void;
  required?: boolean;
  startFieldProps?: CustomizableFieldProps;
  value: DateRangeValue;
  valueTransform?: (value: DateRangeValue) => DateRangeValue;
}

function defaultValueTransform(value: DateRangeValue): DateRangeValue {
  const [start, end] = value;
  return [start, end ? endOfDay(end) : null];
}

const DATE_FORMAT = 'PP';

// defined based on MUI's default min / max
// github.com/mui/mui-x/blob/f074f2735552161fd86d07cca5154e2bcabb263f/packages/x-date-pickers/src/LocalizationProvider/LocalizationProvider.tsx#L126-L127
const DEFAULT_MIN_DATE = new Date('1900-01-01T00:00:00.000Z');
const DEFAULT_MAX_DATE = new Date('2099-12-31T00:00:00.000Z');

export const U21DateRangePicker = (props: U21DateRangePickerProps) => {
  const {
    autoFocus,
    backdrop,
    backdropProps,
    clearable = true,
    disabled,
    endFieldProps,
    error,
    id,
    label = '',
    maxDate = DEFAULT_MAX_DATE,
    maxDateInputProps = {},
    minDate = DEFAULT_MIN_DATE,
    minDateInputProps = {},
    name,
    onBlur,
    onChange,
    onFocus,
    required,
    startFieldProps,
    value,
    valueTransform = defaultValueTransform,
    ...rest
  } = props;

  const onChangeWrapper = (rawValue: DateRangeValue) => {
    const newValue = valueTransform(rawValue);
    trackEvent(AnalyticsEvents.U21DATEPICKER_ON_CHANGE, props, {}, newValue);
    onChange(newValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateRangePicker
        label={label}
        minDate={minDate}
        maxDate={maxDate}
        onChange={onChangeWrapper}
        slots={{
          leftArrowIcon: IconChevronLeft,
          rightArrowIcon: IconChevronRight,
          switchViewIcon: IconChevronDown,
          field: DateRangeInputField,
          day: RangePickersDay,
        }}
        slotProps={{
          desktopPaper: {
            // prevent input from losing focus when clicking on popup
            onMouseDown: (e) => e.preventDefault(),
          },
          popper: {
            placement: 'bottom',
            // must be greater than backdrop
            // number chosen based on https://mui.com/material-ui/customization/z-index
            ...(backdrop ? { style: { zIndex: 1400 } } : {}),
          },
          field: {
            autoFocus,
            backdrop,
            backdropProps,
            clearable,
            dateFormat: DATE_FORMAT,
            disabled,
            endFieldProps,
            error,
            idCustom: id,
            label,
            maxDate,
            maxDateInputProps,
            minDate,
            minDateInputProps,
            name,
            onBlurCustom: onBlur,
            onChange: onChangeWrapper,
            onFocus,
            required,
            startFieldProps,
            value,
          } satisfies ComponentProps<typeof DateRangeInputField> as any,
        }}
        value={[value[0] ?? null, value[1] ?? null]}
        {...getDOMProps(rest)}
      />
    </LocalizationProvider>
  );
};
